'use client'
/* eslint-disable @typescript-eslint/no-misused-promises */

import { couponType } from '@/lib/coupon/server'
import { serviceType } from '@/lib/service/server'
import { usePathname } from 'next/navigation'
import { useCallback, useEffect, useState } from 'react'

type WelcomeCouponProps = {
  service: serviceType
  coupon: couponType
}

export const WelcomeCouponModal: React.FC<WelcomeCouponProps> = ({
  service,
  coupon,
}) => {
  const [isActive, setIsActive] = useState(false)
  const [buttonText, setButtonText] = useState('クーポンを取得する')
  const [isDisabled, setIsDisabled] = useState(false)
  const pathname = usePathname()

  // 設定
  const CONFIG = {
    DELAY_MS: 20000, // 20秒
    STORAGE_KEY: `welcomeCouponShown_${service.service_id}`,
    TIMER_KEY: `welcomeCouponTimer_${service.service_id}`,
    API_ENDPOINT: '/api/coupon',
  }

  // 新規ユーザーチェック
  const isNewUser = useCallback(() => {
    try {
      return !localStorage.getItem(CONFIG.STORAGE_KEY)
    } catch (e) {
      console.error('LocalStorage access error:', e)
      return false
    }
  }, [CONFIG.STORAGE_KEY])

  // タイマー開始時刻を保存
  const saveTimerStart = useCallback(() => {
    try {
      localStorage.setItem(CONFIG.TIMER_KEY, Date.now().toString())
    } catch (e) {
      console.error('LocalStorage save error:', e)
    }
  }, [CONFIG.TIMER_KEY])

  // タイマー開始時刻を取得
  const getTimerStart = useCallback(() => {
    try {
      const start = localStorage.getItem(CONFIG.TIMER_KEY)
      return start ? parseInt(start, 10) : null
    } catch (e) {
      console.error('LocalStorage access error:', e)
      return null
    }
  }, [CONFIG.TIMER_KEY])

  // タイマーをクリア
  const clearTimer = useCallback(() => {
    try {
      localStorage.removeItem(CONFIG.TIMER_KEY)
    } catch (e) {
      console.error('LocalStorage remove error:', e)
    }
  }, [CONFIG.TIMER_KEY])

  // モーダル表示
  const showModal = useCallback(() => {
    setIsActive(true)
    document.body.style.overflow = 'hidden'
  }, [])

  // モーダル非表示
  const hideModal = useCallback(() => {
    setIsActive(false)
    document.body.style.overflow = ''
  }, [])

  // LocalStorageにフラグを保存
  const markAsShown = useCallback(() => {
    try {
      localStorage.setItem(CONFIG.STORAGE_KEY, 'true')
      clearTimer()
    } catch (e) {
      console.error('LocalStorage save error:', e)
    }
  }, [CONFIG.STORAGE_KEY, clearTimer])

  // クーポン取得API呼び出し
  const getCoupon = async () => {
    setIsDisabled(true)
    setButtonText('取得中...')

    try {
      const response = await fetch(CONFIG.API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          couponCode: coupon.coupon_code,
        }),
      })

      if (!response.ok) {
        throw new Error('クーポンの取得に失敗しました')
      }

      await response.json()

      setButtonText('✓ 取得完了！')

      setTimeout(() => {
        hideModal()
        markAsShown()
      }, 1500)
    } catch (error) {
      console.error('Coupon API error:', error)
      setButtonText('エラーが発生しました')

      setTimeout(() => {
        setButtonText('クーポンを取得する')
        setIsDisabled(false)
      }, 2000)
    }
  }

  // モーダルを閉じる
  const handleClose = () => {
    hideModal()
    markAsShown()
  }

  // タイマー管理
  useEffect(() => {
    if (!isNewUser()) {
      return
    }

    const timerStart = getTimerStart()
    const now = Date.now()

    if (timerStart === null) {
      // 初回訪問時: タイマー開始
      saveTimerStart()

      const timerId = setTimeout(() => {
        showModal()
      }, CONFIG.DELAY_MS)

      return () => clearTimeout(timerId)
    } else {
      // 既にタイマーが開始されている場合: 残り時間を計算
      const elapsed = now - timerStart
      const remaining = CONFIG.DELAY_MS - elapsed

      if (remaining <= 0) {
        // 既に時間が経過している場合: すぐに表示
        showModal()
      } else {
        // 残り時間後に表示
        const timerId = setTimeout(() => {
          showModal()
        }, remaining)

        return () => clearTimeout(timerId)
      }
    }
  }, [isNewUser, getTimerStart, saveTimerStart, showModal, CONFIG.DELAY_MS])

  // ESCキーで閉じる
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isActive) {
        handleClose()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isActive])

  // 特定のページでは表示しない（オプション）
  useEffect(() => {
    const excludedPaths = ['/checkout', '/order-complete', '/thank-you']
    if (excludedPaths.some((path) => pathname.includes(path))) {
      hideModal()
    }
  }, [pathname, hideModal])

  if (!isActive) {
    return null
  }

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 animate-fadeIn"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          handleClose()
        }
      }}
    >
      <div className="relative w-[90%] max-w-[500px] rounded-3xl p-8 md:p-10 text-[#333] bg-white text-center shadow-2xl animate-slideUp">
        {/* 閉じるボタン */}
        <button
          onClick={handleClose}
          className="absolute text-[#333] top-4 right-4 w-9 h-9 flex items-center justify-center bg-white/20 hover:bg-white/30 rounded-full text-2xl transition-all duration-300"
          aria-label="閉じる"
        >
          ×
        </button>

        {/* タイトル */}
        <div className="mb-4 text-center">
          <h3 className="text-lg md:text-2xl font-bold mb-2">
            新規ユーザー限定クーポン！
          </h3>
          <p className="text-base md:text-lg opacity-90">
            {coupon.discount_type === 'percentage'
              ? `期間限定! ${coupon.discount_value}%OFFクーポンです`
              : `期間限定! ${coupon.discount_value.toLocaleString()}円OFFクーポンです`}
          </p>
          <p className="text-sm md:text-base opacity-80 mt-2">
            取得後、決済画面で自動適用されます
          </p>
        </div>

        {/* クーポンコード表示 */}
        <div className="bg-white rounded-xl p-5 mb-6 shadow-lg">
          <div
            className="text-xl md:text-4xl font-bold tracking-wider font-mono mb-2"
            style={{ color: service.main_color || '#667eea' }}
          >
            <span className="mr-2 font-light text-sm text-[#333]">コード:</span>
            {coupon.coupon_code}
          </div>
        </div>

        {/* 最低注文金額の表示 */}
        {coupon.minimum_order_amount > 0 && (
          <p className="text-sm opacity-80 mb-6">
            ※ {coupon.minimum_order_amount.toLocaleString()}
            円以上のご注文で利用可能
          </p>
        )}

        {/* 有効期限の表示 */}
        {coupon.valid_until && (
          <p className="text-xs opacity-70 mb-6">
            有効期限:{' '}
            {new Date(coupon.valid_until).toLocaleDateString('ja-JP', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          </p>
        )}

        {/* ボタン */}
        <button
          onClick={getCoupon}
          disabled={isDisabled}
          className="button-color main-color text-white w-full md:w-auto px-10 py-4 bg-white rounded-full text-base font-bold shadow-lg transition-all duration-300 hover:transform hover:-translate-y-1 hover:shadow-xl disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
        >
          {buttonText}
        </button>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(50px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.3s ease-in-out;
        }

        .animate-slideUp {
          animation: slideUp 0.4s ease-out;
        }
      `}</style>
    </div>
  )
}
